import { InMemoryCache } from '@apollo/client'
import { Button, DialogActions, InputLabel, TextField } from '@mui/material'
import omit from 'lodash/omit'
import React from 'react'
import { Mutation } from 'react-apollo'
import NewSelect, { NewOptionType } from '../../../common/select/NewSelect'
import { i18n } from '../../index'
import css from './incentiveProductForm.css'
import {
  createSalesIncentiveProductMutation,
  fetchStationSalesIncentiveProducts,
  salesIncentiveProductsQuery,
  updateSalesIncentiveProductMutation,
} from './incentiveProductQueries'
import { IncentiveProductCategory, SalesIncentiveProduct } from './incentiveProductTypes'

const CATEGORIES: NewOptionType[] = [
  { label: 'Yleinen', value: 'Yleinen' },
  { label: 'Market', value: 'Market' },
  { label: 'Ravintola', value: 'Ravintola' },
  { label: 'Hesburger', value: 'Hesburger' },
  { label: 'Muu', value: 'Muu' },
]

const defaultFormData: FormData = {
  id: null,
  name: '',
  euros: '0',
  cents: '0',
  category: CATEGORIES[0].value as IncentiveProductCategory,
  ean: '',
  cdtPath: '',
  sokId: '',
}

type Data<FieldName extends string = string> = {
  data: Record<FieldName, SalesIncentiveProduct>
}

const updateCacheOnUpdate =
  (query, fieldName: string, variables?: Record<string, any>) =>
  (cache: InMemoryCache, info: Data<'updateSalesIncentiveProduct'>) => {
    const {
      data: { updateSalesIncentiveProduct },
    } = info
    const data = cache.readQuery({
      query,
      variables,
    })
    cache.writeQuery({
      query,
      variables,
      data: {
        ...(data || {}),
        [fieldName]: data?.[fieldName]?.map((ts) => {
          return ts.id === updateSalesIncentiveProduct.id ? updateSalesIncentiveProduct : ts
        }),
      },
    })
  }

const updateCacheOnCreate =
  (query, fieldName: string, variables?: Record<string, any>) =>
  (cache: InMemoryCache, info: Data<'createSalesIncentiveProduct'>) => {
    const {
      data: { createSalesIncentiveProduct },
    } = info
    const data = cache.readQuery({
      query,
      variables,
    })
    cache.writeQuery({
      query,
      variables,
      data: {
        ...(data || {}),
        [fieldName]: [...(data?.[fieldName] || []), createSalesIncentiveProduct],
      },
    })
  }

type Props = {
  data: SalesIncentiveProduct | null | undefined
  date?: Date
  stationId?: string
  onSubmit: (...args: Array<any>) => any
  onCancel: (...args: Array<any>) => any
}

type FormData = {
  id: string | null | undefined
  name: string
  euros: string
  cents: string
  ean: string
  cdtPath: string
  sokId: string
  category: IncentiveProductCategory
}

type State = {
  formData: FormData
}

class IncentiveProductForm extends React.Component<Props, State> {
  state: State = {
    formData: { ...defaultFormData },
  }

  componentDidMount() {
    const { data } = this.props

    if (data) {
      this.setStateFromData(data)
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { data } = this.props

    if (data && JSON.stringify(data) !== JSON.stringify(prevProps.data)) {
      this.setStateFromData(data)
    }
  }

  setStateFromData = (data: SalesIncentiveProduct) => {
    this.setState({
      formData: {
        ...defaultFormData,
        id: data.id,
        name: data.name,
        euros: data.price.toString().split('.')[0],
        cents: this.addTrailingZero(data.price.toString().split('.')[1]),
        category: data.category,
        cdtPath: data.cdtPath ? data.cdtPath : '',
        sokId: data.sokId ? data.sokId : '',
        ean: data.ean ? data.ean : '',
      },
    })
  }

  addTrailingZero = (cents: string) => {
    if (!cents) {
      return '0'
    }

    return cents.length === 1 ? `${cents}0` : cents
  }

  addLeadingZero(cents: string) {
    if (!cents) {
      return '0'
    }

    return cents.length === 1 ? `0${cents}` : cents
  }

  onInput = (fieldName: keyof FormData, newValue: (string | null | undefined) | number) => {
    this.setState({
      formData: { ...this.state.formData, [fieldName]: newValue },
    })
  }

  onSubmit = (onMutate: (...args: Array<any>) => any, e: any) => {
    e.preventDefault()
    const { stationId, date } = this.props
    const formData = {
      ...this.state.formData,
      id: this.state.formData.id,
      name: this.state.formData.name,
      price: Number(`${this.state.formData.euros}.${this.addLeadingZero(this.state.formData.cents)}`),
      category: this.state.formData.category,
    }

    const variables = stationId ? { stationId, date } : undefined

    const query = stationId ? fetchStationSalesIncentiveProducts : salesIncentiveProductsQuery
    const fieldName = stationId ? 'stationSalesIncentiveProducts' : 'salesIncentiveProducts'
    onMutate(
      formData.id
        ? {
            variables: { ...formData },
            update: updateCacheOnUpdate(query, fieldName, variables),
          }
        : {
            variables: stationId ? { ...omit(formData, ['id']), stationId } : { ...omit(formData, ['id']) },
            update: updateCacheOnCreate(query, fieldName, variables),
          },
    )
    this.props.onSubmit()
    this.onReset()
  }

  onReset = () => {
    this.setState({
      formData: { ...defaultFormData },
    })
  }

  onCategoryChange = (value: string | number) => {
    this.setState({
      formData: { ...this.state.formData, category: value as IncentiveProductCategory },
    })
  }

  getOptionLabel = (option: NewOptionType): string => {
    return option.value ? i18n.t(`admin.incentiveProducts.label.${(option.value as string)?.toLowerCase() ?? ''}`) : ''
  }

  render() {
    const { onCancel } = this.props
    const { formData } = this.state
    return (
      <Mutation mutation={formData.id ? updateSalesIncentiveProductMutation : createSalesIncentiveProductMutation}>
        {(onMutate) => (
          <form onSubmit={(e) => this.onSubmit(onMutate, e)} className={css.component}>
            <InputLabel id='categorySelectLabel' shrink>
              {i18n.t('admin.incentiveProducts.label.category')}
            </InputLabel>
            <NewSelect
              options={CATEGORIES}
              value={formData.category}
              className={css.categorySelect}
              getOptionLabel={this.getOptionLabel}
              onChange={this.onCategoryChange}
              placeholder={i18n.t('admin.incentiveProducts.label.placeholder')}
            />

            <div className={css.fields}>
              <div className={css.row}>
                <div className={css.col}>
                  <InputLabel id='nameLabel' shrink>
                    {i18n.t('admin.incentiveProducts.label.name')}
                  </InputLabel>
                  <TextField
                    autoFocus
                    required // label={i18n.t('admin.incentiveProducts.label.name')}
                    value={formData.name}
                    onChange={(e) => this.onInput('name', e.target.value)}
                    className={css.textField}
                  />
                </div>
                <div className={css.col}>
                  <InputLabel id='eanLabel' shrink>
                    {i18n.t('admin.incentiveProducts.label.ean')}
                  </InputLabel>
                  <TextField // label={i18n.t('admin.incentiveProducts.label.ean')}
                    value={formData.ean}
                    className={css.textField}
                    disabled
                  />
                </div>
              </div>

              <div className={css.row}>
                <div className={css.col}>
                  <InputLabel id='cdtPathLabel' shrink>
                    {i18n.t('admin.incentiveProducts.label.cdtPath')}
                  </InputLabel>
                  <TextField // label={i18n.t('admin.incentiveProducts.label.cdtPath')}
                    value={formData.cdtPath}
                    className={css.textField}
                    disabled
                  />
                </div>
                <div className={css.col}>
                  <InputLabel id='sokIdLabel' shrink>
                    {i18n.t('admin.incentiveProducts.label.sokId')}
                  </InputLabel>
                  <TextField // label={i18n.t('admin.incentiveProducts.label.sokId')}
                    value={formData.sokId}
                    className={css.textField}
                    disabled
                  />
                </div>
              </div>

              <div className={css.row}>
                <div className={css.col}>
                  <InputLabel id='eurosLabel' shrink>
                    {i18n.t('admin.incentiveProducts.label.euros')}
                  </InputLabel>
                  <TextField
                    required // label={i18n.t('admin.incentiveProducts.label.euros')}
                    value={formData.euros}
                    onChange={(e) => this.onInput('euros', e.target.value)}
                    className={css.textField}
                    inputProps={{
                      pattern: '^[0-9]{1,3}?$',
                    }} // allow max 3 digits
                  />
                </div>
                <div className={css.col}>
                  <InputLabel id='centsLabel' shrink>
                    {i18n.t('admin.incentiveProducts.label.cents')}
                  </InputLabel>
                  <TextField
                    required // label={i18n.t('admin.incentiveProducts.label.cents')}
                    value={formData.cents}
                    onChange={(e) => this.onInput('cents', e.target.value)}
                    className={css.textField}
                    inputProps={{
                      pattern: '^[0-9]{1,2}?$',
                    }} // allow max 2 digits
                  />
                </div>
              </div>
            </div>

            <DialogActions className={css.actions}>
              <Button type='button' onClick={onCancel}>
                {i18n.t('buttons.cancel')}
              </Button>
              <Button type='submit' color='primary'>
                {i18n.t('buttons.save')}
              </Button>
            </DialogActions>
          </form>
        )}
      </Mutation>
    )
  }
}

export default IncentiveProductForm
